import { alpha, Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../currency/useMoney"
import { LocalizedStrict } from "../../localization/components/AppLocalized"

import {
    MENU_DISPLAY_BODY_FONT,
    MENU_DISPLAY_COLORS,
    MENU_DISPLAY_DISPLAY_FONT,
    MenuDisplayItem,
    menuDisplayPriceParts,
} from "./menuDisplayData"

const DESCRIPTION_LINES = 3

const useStyles = makeStyles()((theme) => ({
    slide: {
        flexShrink: 0,
        width: "100%",
        boxSizing: "border-box",
        padding: "0 .25em 0 3.5em",
    },
    topRow: {
        minHeight: "22em",
        flexShrink: 0,
    },
    imageWrap: {
        position: "relative",
        flexShrink: 0,
        width: "37.125em",
        height: "22em",
    },
    imageClip: {
        width: "100%",
        height: "100%",
        borderRadius: "1.5em",
        border: `.1875em solid ${theme.palette.tailwind.slate[200]}`,
        overflow: "hidden",
    },
    image: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
    },
    priceBubble: {
        width: "12.8125em",
        height: "12.8125em",
        borderRadius: "50%",
        border: `.3425em solid ${MENU_DISPLAY_COLORS.darkGreen}`,
        backgroundColor: MENU_DISPLAY_COLORS.cream,
        boxShadow: `0 1.19875em .685em 0 ${alpha(theme.palette.common.black, 0.25)}`,
        transform: "rotate(-13.03deg)",
    },
    priceBubbleOverlay: {
        position: "absolute",
        bottom: "-.625em",
        left: "1em",
    },
    priceBubbleStandalone: {
        flexShrink: 0,
        width: "15.625em",
        height: "15.625em",
        margin: "0 3.5em 0 1em",
    },
    priceAmount: {
        fontFamily: MENU_DISPLAY_DISPLAY_FONT,
        fontWeight: 700,
        fontSize: "4.625em",
        lineHeight: 1,
        letterSpacing: 0,
        color: MENU_DISPLAY_COLORS.darkGreen,
    },
    priceCents: {
        fontFamily: MENU_DISPLAY_BODY_FONT,
        fontWeight: 700,
        fontSize: "2em",
        lineHeight: 1,
        letterSpacing: 0,
        color: MENU_DISPLAY_COLORS.darkGreen,
    },
    priceCurrency: {
        fontFamily: MENU_DISPLAY_BODY_FONT,
        fontWeight: 600,
        fontSize: "2.75em",
        lineHeight: 1,
        letterSpacing: 0,
        textTransform: "uppercase",
        color: MENU_DISPLAY_COLORS.darkGreen,
    },
    info: {
        marginLeft: "3.5em",
        minWidth: 0,
    },
    infoNoImage: {
        marginLeft: 0,
        flex: "1 1 auto",
    },
    pill: {
        alignSelf: "flex-start",
        minHeight: "3.8125em",
        boxSizing: "border-box",
        padding: "1em 1.5em",
        marginBottom: "2em",
        borderRadius: "3.75em",
        border: `.1875em solid ${MENU_DISPLAY_COLORS.darkGreen}`,
    },
    pillText: {
        fontWeight: 600,
        fontSize: "1.5em",
        lineHeight: 1,
        letterSpacing: 0,
        textAlign: "center",
        textTransform: "uppercase",
        color: theme.palette.tailwind.slate[700],
    },
    name: {
        fontFamily: MENU_DISPLAY_DISPLAY_FONT,
        fontWeight: 600,
        fontSize: "5.5em",
        lineHeight: 1.1364,
        letterSpacing: 0,
        textTransform: "uppercase",
        color: MENU_DISPLAY_COLORS.darkGreen,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        overflowWrap: "anywhere",
        wordBreak: "normal",
    },
    nameNoTag: {
        WebkitLineClamp: 3,
    },
    descriptionBlock: {
        marginTop: "1.5em",
        height: `${DESCRIPTION_LINES * 3.5}em`,
        flexShrink: 0,
    },
    description: {
        fontWeight: 500,
        fontSize: "2.5em",
        lineHeight: 1.4,
        letterSpacing: 0,
        color: theme.palette.tailwind.slate[700],
        display: "-webkit-box",
        WebkitLineClamp: DESCRIPTION_LINES,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    allergensBox: {
        boxSizing: "border-box",
        marginTop: "1.5em",
        height: "13.75em",
        padding: "1.25em 1.75em",
        borderRadius: "1.5em",
        border: `.1875em solid ${MENU_DISPLAY_COLORS.darkGreen}`,
        overflow: "hidden",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        alignContent: "flex-start",
        gap: ".75em 1em",
    },
    containsLabel: {
        alignSelf: "center",
        marginRight: "1em",
        fontWeight: 700,
        fontSize: "1.75em",
        lineHeight: 1.5714,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        color: MENU_DISPLAY_COLORS.darkGreen,
    },
    allergenChip: {
        boxSizing: "border-box",
        height: "2.75em",
        padding: ".25em 1.25em .25em .25em",
        borderRadius: "2.75em",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    allergenBadge: {
        width: "2.25em",
        height: "2.25em",
        borderRadius: "50%",
        backgroundColor: MENU_DISPLAY_COLORS.darkGreen,
    },
    allergenBadgeLetter: {
        fontWeight: 700,
        fontSize: "1.25em",
        lineHeight: 1,
        letterSpacing: 0,
        color: theme.palette.common.white,
    },
    allergenName: {
        fontWeight: 700,
        fontSize: "1.5em",
        lineHeight: 1,
        letterSpacing: 0,
        color: theme.palette.tailwind.slate[700],
    },
}))

type MenuDisplaySlideProps = Readonly<{
    item: MenuDisplayItem
}>

export function MenuDisplaySlide({ item }: MenuDisplaySlideProps) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})

    const price = menuDisplayPriceParts(item.price, moneyFactory)

    const priceBubble = (overlay: boolean) =>
        price && (
            <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                className={cx(
                    classes.priceBubble,
                    overlay ? classes.priceBubbleOverlay : classes.priceBubbleStandalone,
                )}
            >
                <Stack direction="row" alignItems="baseline">
                    <Typography className={classes.priceAmount}>{price.whole}</Typography>
                    <Typography className={classes.priceCents}>
                        {price.separator}
                        {price.cents}
                    </Typography>
                </Stack>
                <Typography className={classes.priceCurrency}>{price.currency}</Typography>
            </Stack>
        )

    return (
        <Stack direction="column" className={classes.slide}>
            <Stack direction="row" alignItems={item.imageUrl ? "flex-start" : "center"} className={classes.topRow}>
                {item.imageUrl && (
                    <Box className={classes.imageWrap}>
                        <Box className={classes.imageClip}>
                            <img className={classes.image} src={item.imageUrl} alt={item.name} />
                        </Box>
                        {priceBubble(true)}
                    </Box>
                )}
                {!item.imageUrl && priceBubble(false)}
                <Stack direction="column" className={cx(classes.info, !item.imageUrl && classes.infoNoImage)}>
                    {item.tag && (
                        <Stack direction="row" alignItems="center" className={classes.pill}>
                            <Typography className={classes.pillText}>{item.tag}</Typography>
                        </Stack>
                    )}
                    <Typography className={cx(classes.name, !item.tag && classes.nameNoTag)}>{item.name}</Typography>
                </Stack>
            </Stack>
            <Box className={classes.descriptionBlock}>
                <Typography className={classes.description}>{item.description}</Typography>
            </Box>
            {item.allergens.length > 0 && (
                <Box className={classes.allergensBox}>
                    <LocalizedStrict id="public-menu-display-allergens-contains-label">
                        <Typography className={classes.containsLabel}>Contains</Typography>
                    </LocalizedStrict>
                    {item.allergens.map((allergen) => (
                        <Stack
                            key={allergen}
                            direction="row"
                            alignItems="center"
                            spacing=".625em"
                            className={classes.allergenChip}
                        >
                            <Stack alignItems="center" justifyContent="center" className={classes.allergenBadge}>
                                <Typography className={classes.allergenBadgeLetter}>
                                    {allergen.charAt(0).toUpperCase()}
                                </Typography>
                            </Stack>
                            <Typography className={classes.allergenName}>{allergen}</Typography>
                        </Stack>
                    ))}
                </Box>
            )}
        </Stack>
    )
}
